import type { PriceId } from '@iheartjane/types';
import orderBy from 'lodash-es/orderBy';

import { TERPENE_COLORS } from '@jane/search/util';
import type { FlavorEnum, LabResult, TerpeneEnum } from '@jane/shared/types';
import { TerpeneEnumValues } from '@jane/shared/types';

const TERPENE_COLORS_TO_FLAVORS = {
  gold: 'citrusy',
  'ember-dark': 'spicy',
  pacific: 'floral',
  success: 'hoppy',
  'tangerine-dark': 'earthy',
  emerald: 'herbal',
};

type LabResultWithTerpene = LabResult['lab_results'][number] & {
  matchingTerpene: TerpeneEnum;
};

const getTerpenesFromLabResults = (
  labResults: LabResult[],
  weight: PriceId
): LabResultWithTerpene[] => {
  const labResult = labResults?.find(({ price_id }) => price_id === weight);

  if (!labResult) {
    return [];
  }

  return labResult.lab_results
    .filter(({ value }) => (value || 0) > 0)
    .reduce(
      (
        prev: LabResultWithTerpene[],
        curr: LabResult['lab_results'][number]
      ) => {
        const compoundName = curr.compound_name.toLowerCase();

        const matchingTerpene = TerpeneEnumValues.find((terpene) =>
          compoundName.endsWith(terpene)
        );

        if (!matchingTerpene) {
          return prev;
        }

        return [...prev, { ...curr, matchingTerpene }];
      },
      []
    );
};

export const getTopFlavors = (labResults: LabResult[], weight: PriceId) => {
  const flavors: { [flavor: string]: number } = {};
  const terpenes = getTerpenesFromLabResults(labResults, weight);

  terpenes.forEach((terpene) => {
    const terpeneColor = TERPENE_COLORS[
      terpene.matchingTerpene
    ] as keyof typeof TERPENE_COLORS_TO_FLAVORS;
    const flavor = TERPENE_COLORS_TO_FLAVORS[terpeneColor];

    if (flavor) {
      flavors[flavor] = (flavors[flavor] || 0) + (terpene.value || 0);
    }
  });

  return Object.entries(flavors)
    .sort(([, valueA], [, valueB]) => valueB - valueA)
    .slice(0, 3)
    .map(([key, value]) => ({ value: key as FlavorEnum, count: value }));
};

export const getSortedTerpenes = (labResults: LabResult[], weight: PriceId) =>
  orderBy(
    getTerpenesFromLabResults(labResults, weight),
    ['value', 'compound_name'],
    ['desc', 'asc']
  );
